import { supabase } from "../../../lib/supabase";
import {
  LeanCustomsService,
  type LeanCustomsDeclaration,
} from "../../../services/leancustomsService";
import { searchMatchingLeanCustomsDeclarations } from "../../../services/leanCustomsBookingSearch";
import {
  pickDeclarationsForBooking,
  pickDeclarationsForLinkedOrder,
} from "../../../utils/leanCustomsDeclarationLookup";
import { formatLeanCustomsOrderDetailLine } from "../../../utils/leanCustomsOrderDisplay";
import { pickString } from "../../../utils/leanCustomsRecordUtils";
import { extractUtbTtwAmountDisplays } from "../../../utils/leanCustomsUtbTtwAmounts";
import { getDeclarationDocumentOfficialKind } from "../../../utils/leanCustomsDeclarationDocumentsSort";
import { DECLARATION_STATUS_LABELS } from "../../../types/leancustoms";
import { formatBookingEffLabel } from "./botAdminShared";
import type { BookingInsight } from "./botAdminShared";

export type BotLeanCustomsPayload = {
  linked: {
    order_id: string | null;
    declaration_id: string | null;
  };
  order: Record<string, unknown> | null;
  declaration: Record<string, unknown> | null;
  documents: Array<Record<string, unknown>>;
  invoices: Array<Record<string, unknown>>;
  amounts: {
    utb_payment: string | null;
    ttw_surety: string | null;
  };
  warnings: string[];
  available: boolean;
};

function slimOrder(order: Record<string, unknown>): Record<string, unknown> {
  return {
    id: order.id ?? null,
    reference: pickString(
      order.reference,
      order.customer_reference,
      order.booking_number,
    ),
    status:
      pickString(
        order.status,
        order.order_status,
        order.workflow_status,
        order.state,
      ) ?? null,
    detail_line: formatLeanCustomsOrderDetailLine(order),
    ticket_number: order.ticket_number ?? null,
    order_type: order.order_type ?? null,
    transport_mode: order.transport_mode ?? null,
    incoterm: order.incoterm_code ?? order.incoterm ?? null,
  };
}

function slimDeclaration(
  declaration: LeanCustomsDeclaration,
): Record<string, unknown> {
  const dms = declaration.dms_status || "unknown";
  return {
    id: declaration.id ?? null,
    uid: declaration.uid ?? null,
    reference: declaration.reference ?? null,
    mrn: declaration.mrn ?? null,
    lrn: declaration.lrn ?? null,
    dms_status: dms,
    dms_status_label: DECLARATION_STATUS_LABELS[dms] || dms,
    declaration_type: declaration.declaration_type ?? null,
    acceptance_date: declaration.acceptance_date ?? null,
    clearance_date: declaration.clearance_date ?? null,
    release_date: declaration.release_date ?? null,
    ticket_number: declaration.ticket_number ?? null,
  };
}

/**
 * Load LeanCustoms when the booking has linked IDs, or the company has
 * LeanCustoms configured (then search by EFF reference like the booking view).
 */
export async function fetchBotLeanCustoms(
  booking: BookingInsight,
): Promise<BotLeanCustomsPayload | null> {
  const companyId =
    typeof booking.company_id === "number"
      ? booking.company_id
      : Number(booking.company_id);
  if (!Number.isFinite(companyId)) return null;

  const linkedOrderId =
    typeof booking.leancustoms_order_id === "string"
      ? booking.leancustoms_order_id.trim() || null
      : null;
  const linkedDeclarationId =
    typeof booking.leancustoms_declaration_id === "string"
      ? booking.leancustoms_declaration_id.trim() || null
      : null;

  const { data: companyRow, error: companyError } = await supabase
    .from("company")
    .select("leancustoms_organization_id")
    .eq("id", companyId)
    .maybeSingle();

  if (companyError) {
    console.warn("LeanCustoms company lookup:", companyError);
  }

  const organizationId =
    typeof companyRow?.leancustoms_organization_id === "string"
      ? companyRow.leancustoms_organization_id.trim() || null
      : null;

  if (!linkedOrderId && !linkedDeclarationId && !organizationId) {
    return null;
  }

  const warnings: string[] = [];
  let declId = linkedDeclarationId;
  let order: Record<string, unknown> | null = null;

  const bookingReference = formatBookingEffLabel({
    id: booking.id,
    reference: booking.reference,
    transport_type: booking.transport_type,
  });

  try {
    if (!declId && organizationId) {
      const candidates = await searchMatchingLeanCustomsDeclarations(
        organizationId,
        companyId,
        booking.id,
        bookingReference,
        null,
        linkedOrderId,
      );
      const picked = linkedOrderId
        ? pickDeclarationsForLinkedOrder(candidates, linkedOrderId, booking.id)
        : pickDeclarationsForBooking(candidates, booking.id);
      declId = picked[0]?.id ?? null;
    }

    if (linkedOrderId) {
      const ordRes = await LeanCustomsService.getOrder(linkedOrderId, companyId);
      if (ordRes.success && ordRes.data) {
        order = slimOrder(ordRes.data);
      } else if (ordRes.error) {
        warnings.push(ordRes.error);
      }
    }

    if (!declId && !order) {
      return {
        linked: {
          order_id: linkedOrderId,
          declaration_id: linkedDeclarationId,
        },
        order: null,
        declaration: null,
        documents: [],
        invoices: [],
        amounts: { utb_payment: null, ttw_surety: null },
        warnings,
        available: false,
      };
    }

    if (!declId) {
      return {
        linked: {
          order_id: linkedOrderId,
          declaration_id: linkedDeclarationId,
        },
        order,
        declaration: null,
        documents: [],
        invoices: [],
        amounts: { utb_payment: null, ttw_surety: null },
        warnings,
        available: Boolean(order),
      };
    }

    const [decRes, docRes, invRes] = await Promise.all([
      LeanCustomsService.getDeclaration(declId, companyId),
      LeanCustomsService.listDeclarationDocuments(declId, companyId),
      LeanCustomsService.listDeclarationInvoices(declId, companyId),
    ]);

    const declaration = decRes.success ? (decRes.data ?? null) : null;
    if (!decRes.success && decRes.error) warnings.push(decRes.error);

    const documents =
      docRes.success && docRes.data?.results
        ? docRes.data.results.map((doc) => ({
            id: doc.id,
            filename: doc.filename ?? null,
            content_type: doc.content_type ?? null,
            byte_size: doc.byte_size ?? null,
            kind: getDeclarationDocumentOfficialKind(doc),
          }))
        : [];
    if (!docRes.success && docRes.error) warnings.push(docRes.error);

    const invoicesRaw =
      invRes.success && invRes.data?.results ? invRes.data.results : [];
    if (!invRes.success && invRes.error) warnings.push(invRes.error);

    const invoices = invoicesRaw.map((inv) => ({
      id: inv.id,
      status: inv.status ?? null,
      invoice_number: inv.invoice_number ?? null,
      invoice_date: inv.invoice_date ?? null,
      supplier_reference: inv.supplier_reference ?? null,
      customer_reference: inv.customer_reference ?? null,
    }));

    const amounts = extractUtbTtwAmountDisplays(declaration, invoicesRaw);

    return {
      linked: {
        order_id: linkedOrderId,
        declaration_id: declId,
      },
      order,
      declaration: declaration ? slimDeclaration(declaration) : null,
      documents,
      invoices,
      amounts: {
        utb_payment: amounts.utbPayment,
        ttw_surety: amounts.ttwSurety,
      },
      warnings,
      available: Boolean(order || declaration),
    };
  } catch (err) {
    console.warn("LeanCustoms bot enrich failed:", err);
    return {
      linked: {
        order_id: linkedOrderId,
        declaration_id: linkedDeclarationId,
      },
      order: null,
      declaration: null,
      documents: [],
      invoices: [],
      amounts: { utb_payment: null, ttw_surety: null },
      warnings: [
        err instanceof Error ? err.message : "LeanCustoms load failed",
      ],
      available: false,
    };
  }
}
